import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { CheckCircle, Buildings, User, Lock, Briefcase } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Public signup wizard — 3 steps:
//   1. Account (name, email, password)
//   2. Workspace (name, optional phone)
//   3. Plan selection (defaults to Free; paid plans link to billing in Sprint B)
export default function SignupPage() {
  const navigate = useNavigate();
  const { signup, user } = useAuth();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({
    name: '', email: '', password: '',
    workspace_name: '', company_phone: '',
    plan_code: 'free',
  });

  // If already logged in, bounce to dashboard
  useEffect(() => {
    if (user && user.id) navigate('/dashboard', { replace: true });
  }, [user, navigate]);

  // Load plans (uses auth-free fallback so the pricing card still renders pre-signup).
  useEffect(() => {
    const seed = [
      { code: 'free', name: 'Free', price_inr: 0, employee_limit: 5,
        features: ['1 admin + 5 employees', 'Invoices & Clients', 'Attendance & Leave', 'Email support'] },
      { code: 'starter', name: 'Starter', price_inr: 11988, employee_limit: 25,
        features: ['Up to 25 employees', 'Multi-company invoicing', 'Proposal builder + PDF', 'Email & chat support'] },
      { code: 'pro', name: 'Pro', price_inr: 29988, employee_limit: 100, is_recommended: true,
        features: ['Up to 100 employees', 'All Starter features', 'GST reports', 'Project & Task mgmt', 'Permissions tracking', 'Priority support'] },
      { code: 'business', name: 'Business', price_inr: 59988, employee_limit: 500,
        features: ['Up to 500 employees', 'All Pro features', 'Mobile APIs + Swagger', 'Custom RBAC roles', 'Password Vault', 'Dedicated AM'] },
    ];
    setPlans(seed);
    // Fire-and-forget — if a public/plans endpoint becomes available later we'll prefer that.
    axios.get(`${API_URL}/api/workspaces/plans`, { withCredentials: true })
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setPlans(r.data); })
      .catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validStep = () => {
    if (step === 1) {
      if (!form.name.trim() || form.name.trim().length < 2) return 'Please enter your name.';
      if (!/^[^@]+@[^.]+\..+$/.test(form.email)) return 'Please enter a valid email.';
      if (form.password.length < 6) return 'Password must be at least 6 characters.';
    } else if (step === 2) {
      if (!form.workspace_name.trim() || form.workspace_name.trim().length < 2) return 'Please give your workspace a name.';
    }
    return null;
  };

  const handleNext = () => {
    const err = validStep();
    if (err) { toast.error(err); return; }
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    const err = validStep();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      await signup({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        workspace_name: form.workspace_name.trim(),
        plan_code: form.plan_code,
        company_phone: form.company_phone.trim(),
      });
      toast.success(`Welcome to Office Prime, ${form.name}! 🎉`);
      // Free plan goes straight to dashboard. Paid plans go to /billing for Razorpay (Sprint B).
      if (form.plan_code === 'free') {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/billing?from=signup&plan=' + form.plan_code, { replace: true });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
  const monthly = (annualPrice) => annualPrice > 0 ? `${inr(Math.round(annualPrice / 12))}/mo` : 'Free';

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2e4a 100%)' }}>
      <div className="w-full max-w-3xl">
        {/* Brand header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-black text-white tracking-tight">
            <span className="text-cyan-400">office</span> <span className="text-white">prime</span>
          </h1>
          <p className="text-cyan-100/70 text-xs uppercase tracking-widest mt-1">Smart Workspaces, Smarter Business</p>
        </div>

        <Card className="p-6 sm:p-8 shadow-2xl">
          {/* Step indicator */}
          <div className="flex items-center justify-between mb-6">
            {[
              { n: 1, label: 'Account', icon: <User size={14} /> },
              { n: 2, label: 'Workspace', icon: <Buildings size={14} /> },
              { n: 3, label: 'Plan', icon: <Briefcase size={14} /> },
            ].map((s, i) => (
              <React.Fragment key={s.n}>
                <div className={`flex items-center gap-2 ${step >= s.n ? '' : 'opacity-40'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition ${step === s.n ? 'bg-blue-600 text-white' : step > s.n ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                    {step > s.n ? <CheckCircle size={16} weight="fill" /> : s.icon}
                  </div>
                  <span className="text-xs font-semibold hidden sm:inline" style={{ color: step >= s.n ? '#1a2e4a' : '#94a3b8' }}>{s.label}</span>
                </div>
                {i < 2 && <div className={`flex-1 h-0.5 mx-2 ${step > s.n ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* STEP 1 — Account */}
          {step === 1 && (
            <div className="space-y-4" data-testid="signup-step-account">
              <h2 className="text-xl font-bold" style={{ color: '#1a2e4a' }}>Create your account</h2>
              <p className="text-sm text-gray-500">You'll be the admin of your workspace.</p>
              <div>
                <Label htmlFor="signup-name">Full name</Label>
                <Input id="signup-name" data-testid="signup-name" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Jane Doe" />
              </div>
              <div>
                <Label htmlFor="signup-email">Work email</Label>
                <Input id="signup-email" type="email" data-testid="signup-email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@company.com" />
              </div>
              <div>
                <Label htmlFor="signup-password">Password</Label>
                <Input id="signup-password" type="password" data-testid="signup-password" value={form.password} onChange={(e) => set('password', e.target.value)} placeholder="At least 6 characters" />
              </div>
              <div className="flex justify-between items-center pt-2">
                <Link to="/login" className="text-sm text-blue-600 hover:underline">Already have an account? Log in</Link>
                <Button onClick={handleNext} data-testid="signup-next-1">Continue →</Button>
              </div>
            </div>
          )}

          {/* STEP 2 — Workspace */}
          {step === 2 && (
            <div className="space-y-4" data-testid="signup-step-workspace">
              <h2 className="text-xl font-bold" style={{ color: '#1a2e4a' }}>Name your workspace</h2>
              <p className="text-sm text-gray-500">This is the name your team will see. You can change it later.</p>
              <div>
                <Label htmlFor="signup-ws-name">Workspace / Company name</Label>
                <Input id="signup-ws-name" data-testid="signup-ws-name" value={form.workspace_name} onChange={(e) => set('workspace_name', e.target.value)} placeholder="Acme Inc." />
              </div>
              <div>
                <Label htmlFor="signup-phone">Company phone (optional)</Label>
                <Input id="signup-phone" data-testid="signup-phone" value={form.company_phone} onChange={(e) => set('company_phone', e.target.value)} placeholder="+91 98765 43210" />
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="signup-back-2">← Back</Button>
                <Button onClick={handleNext} data-testid="signup-next-2">Continue →</Button>
              </div>
            </div>
          )}

          {/* STEP 3 — Plan */}
          {step === 3 && (
            <div className="space-y-4" data-testid="signup-step-plan">
              <h2 className="text-xl font-bold" style={{ color: '#1a2e4a' }}>Pick a plan</h2>
              <p className="text-sm text-gray-500">Start free. Upgrade any time. All plans billed annually in INR.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {plans.map((p) => {
                  const isSelected = form.plan_code === p.code;
                  return (
                    <button
                      key={p.code}
                      type="button"
                      onClick={() => set('plan_code', p.code)}
                      className={`text-left border-2 rounded-lg p-3 transition relative ${isSelected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                      data-testid={`signup-plan-${p.code}`}
                    >
                      {p.is_recommended && (
                        <span className="absolute -top-2 right-3 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-400 text-amber-900">
                          Most popular
                        </span>
                      )}
                      <div className="flex justify-between items-baseline">
                        <h3 className="font-bold" style={{ color: '#1a2e4a' }}>{p.name}</h3>
                        <span className="text-lg font-black" style={{ color: '#1a2e4a' }}>
                          {monthly(p.price_inr)}
                        </span>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">{p.price_inr > 0 ? `Billed annually: ${inr(p.price_inr)}` : 'No card required'}</div>
                      <div className="text-[10px] font-semibold text-gray-700 mt-1">Up to {p.employee_limit} employees</div>
                      <ul className="mt-2 space-y-0.5">
                        {(p.features || []).slice(0, 4).map((f, i) => (
                          <li key={i} className="text-[11px] text-gray-600 flex items-start gap-1">
                            <CheckCircle size={10} weight="fill" className="text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-between pt-3">
                <Button variant="outline" onClick={() => setStep(2)} data-testid="signup-back-3">← Back</Button>
                <Button onClick={handleSubmit} disabled={submitting} data-testid="signup-submit">
                  {submitting ? 'Creating…' : (form.plan_code === 'free' ? 'Create workspace' : 'Continue to payment')}
                </Button>
              </div>
            </div>
          )}
        </Card>

        <p className="text-center text-xs text-cyan-100/40 mt-6">
          © {new Date().getFullYear()} Office Prime · Smart Workspaces, Smarter Business
        </p>
      </div>
    </div>
  );
}
